import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown, ChevronRight, Building2, AlertOctagon, Clock3,
  ShieldCheck, ShieldAlert, AlertTriangle, ArrowUpRight,
  Users, AlarmClock,
} from "lucide-react";

/**
 * Portfolio-level rollups — one traffic-light row per portfolio for
 * client_admin users overseeing multiple portfolios. Each row is
 * click-to-expand and reveals the site list beneath.
 *
 * Props:
 *   portfolios: [{
 *     portfolio_id, portfolio_name, active_sites, total_sites,
 *     open_incidents, pending_approvals,
 *     health: { score, status },
 *     sites: [{ id, name, status }],
 *   }]
 */
const STATUS_META = {
  green:   { color: "#22C55E", label: "STRONG",  Icon: ShieldCheck,   ring: "#22C55E" },
  yellow:  { color: "#EAB308", label: "WATCH",   Icon: AlertTriangle, ring: "#EAB308" },
  red:     { color: "#EF4444", label: "AT RISK", Icon: ShieldAlert,   ring: "#EF4444" },
  unknown: { color: "#6B7280", label: "PENDING", Icon: ShieldCheck,   ring: "#3F3F46" },
};

function TrafficLight({ status }) {
  const color = (STATUS_META[status] || STATUS_META.unknown).color;
  return (
    <span
      className="inline-block rounded-full"
      style={{
        width: 14, height: 14, backgroundColor: color,
        boxShadow: `0 0 0 3px ${color}22`,
      }}
      aria-label={status}
    />
  );
}

function RollupRow({ row }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const status  = row.health?.status || "unknown";
  const score   = row.health?.score;
  const meta    = STATUS_META[status] || STATUS_META.unknown;
  const Icon    = meta.Icon;
  const canOpen = (row.sites || []).length > 0;

  const openIncidents = (e) => {
    e.stopPropagation();
    const params = new URLSearchParams();
    params.set("portfolio", row.portfolio_id || "unassigned");
    if (row.portfolio_name) params.set("portfolio_name", row.portfolio_name);
    nav(`/portal/reports/incidents?${params.toString()}`);
  };

  return (
    <div
      className="border border-[#27272A] bg-[#0F0F0F] hover:border-[#3F3F46] transition-colors duration-150"
      data-testid={`portfolio-rollup-row-${row.portfolio_id || "unassigned"}`}
    >
      <div
        className={`w-full flex items-center gap-4 p-4 ${canOpen ? "cursor-pointer" : "cursor-default"}`}
        onClick={() => canOpen && setOpen((o) => !o)}
        role={canOpen ? "button" : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onKeyDown={(e) => {
          if (canOpen && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        data-testid={`portfolio-rollup-toggle-${row.portfolio_id || "unassigned"}`}
      >
        {/* Chevron */}
        <span className="flex-shrink-0 text-zinc-500">
          {canOpen
            ? (open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />)
            : <span className="inline-block size-4" />}
        </span>

        {/* Traffic light + portfolio name */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <TrafficLight status={status} />
          <div className="min-w-0">
            <div
              className="font-display uppercase tracking-tight font-bold text-base truncate"
              data-testid={`portfolio-rollup-name-${row.portfolio_id || "unassigned"}`}
            >
              {row.portfolio_name}
            </div>
            <div className="label-mute flex items-center gap-1 mt-0.5">
              <Icon className="size-3" style={{ color: meta.color }} />
              <span>{meta.label}</span>
              {score != null && (
                <span className="ml-1 font-mono text-zinc-400">· {Math.round(score)}/100</span>
              )}
            </div>
          </div>
        </div>

        {/* Counters — compact */}
        <div className="hidden sm:flex items-center gap-6 flex-shrink-0 pr-2">
          <Metric
            icon={Building2}
            accent="#0A84FF"
            value={`${row.active_sites}/${row.total_sites}`}
            label="sites"
            testId={`portfolio-rollup-sites-count-${row.portfolio_id || "unassigned"}`}
          />
          <Metric
            icon={Users}
            accent={row.guards_on_duty > 0 ? "#22C55E" : "#3F3F46"}
            value={row.guards_on_duty ?? 0}
            label="on-duty"
            testId={`portfolio-rollup-on-duty-${row.portfolio_id || "unassigned"}`}
          />
          <Metric
            icon={AlarmClock}
            accent={row.open_tardies > 0 ? "#FF3B30" : "#3F3F46"}
            value={row.open_tardies ?? 0}
            label="tardies"
            testId={`portfolio-rollup-tardies-${row.portfolio_id || "unassigned"}`}
          />
          <Metric
            icon={AlertOctagon}
            accent={row.open_incidents > 0 ? "#EF4444" : "#3F3F46"}
            value={row.open_incidents}
            label="open incidents"
            testId={`portfolio-rollup-incidents-${row.portfolio_id || "unassigned"}`}
          />
          <Metric
            icon={Clock3}
            accent={row.pending_approvals > 0 ? "#EAB308" : "#3F3F46"}
            value={row.pending_approvals}
            label="pending"
            testId={`portfolio-rollup-pending-${row.portfolio_id || "unassigned"}`}
          />
          <button
            type="button"
            onClick={openIncidents}
            data-testid={`portfolio-rollup-view-reports-${row.portfolio_id || "unassigned"}`}
            title={`View reports for ${row.portfolio_name}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest border border-[#27272A] text-zinc-400 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
          >
            View <ArrowUpRight className="size-3" />
          </button>
        </div>
      </div>

      {/* Mobile counters row */}
      <div className="sm:hidden flex items-center gap-4 px-4 pb-4 -mt-2 flex-wrap">
        <Metric icon={Building2}    accent="#0A84FF" value={`${row.active_sites}/${row.total_sites}`} label="sites" />
        <Metric icon={Users}        accent={row.guards_on_duty > 0 ? "#22C55E" : "#3F3F46"} value={row.guards_on_duty ?? 0} label="on-duty" />
        <Metric icon={AlarmClock}   accent={row.open_tardies > 0 ? "#FF3B30" : "#3F3F46"} value={row.open_tardies ?? 0} label="tardies" />
        <Metric icon={AlertOctagon} accent={row.open_incidents > 0 ? "#EF4444" : "#3F3F46"} value={row.open_incidents} label="open" />
        <Metric icon={Clock3}       accent={row.pending_approvals > 0 ? "#EAB308" : "#3F3F46"} value={row.pending_approvals} label="pending" />
        <button
          type="button"
          onClick={openIncidents}
          data-testid={`portfolio-rollup-view-reports-mobile-${row.portfolio_id || "unassigned"}`}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#27272A] text-zinc-400"
        >
          View <ArrowUpRight className="size-3" />
        </button>
      </div>

      {/* Expandable site list */}
      {open && canOpen && (
        <div
          className="border-t border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3"
          data-testid={`portfolio-rollup-sites-panel-${row.portfolio_id || "unassigned"}`}
        >
          <div className="label-mute mb-2">Sites in this portfolio</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {row.sites.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-2 text-sm text-zinc-300 border border-[#1F1F1F] bg-[#121212] px-3 py-2"
                data-testid={`portfolio-rollup-site-${s.id}`}
              >
                <Building2 className="size-3.5 text-zinc-500 flex-shrink-0" />
                <span className="truncate">{s.name}</span>
                {s.status && s.status !== "active" && (
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                    {s.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, accent, value, label, testId }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testId}>
      <Icon className="size-3.5" style={{ color: accent }} />
      <span className="font-mono font-black text-lg tracking-tight" style={{ color: accent }}>
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}

export default function PortfolioRollups({ portfolios = [] }) {
  if (!portfolios.length) return null;

  return (
    <div className="space-y-3" data-testid="portfolio-rollups-section">
      <div className="flex items-center gap-2">
        <div className="font-display uppercase tracking-widest text-sm text-[#FF3B30] font-bold">
          Portfolio Snapshots
        </div>
        <div className="h-px bg-[#27272A] flex-1" />
        <div className="label-mute">
          {portfolios.length} portfolio{portfolios.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="space-y-2">
        {portfolios.map((row) => (
          <RollupRow key={row.portfolio_id || "unassigned"} row={row} />
        ))}
      </div>
    </div>
  );
}
